/**
 * colorblindMatrices — 3×3 colour-transform matrices for colour-vision
 * deficiency (CVD) simulation and daltonization (correction).
 *
 * ── Simulation matrices (Machado, Oliveira & Fernandes 2009) ──────────────
 * We use the severity-1.0 (full dichromacy) sRGB-space simulation matrices
 * from:
 *
 *   G. M. Machado, M. M. Oliveira, L. A. F. Fernandes,
 *   "A Physiologically-based Model for Simulation of Color Vision Deficiency",
 *   IEEE Transactions on Visualization and Computer Graphics, vol. 15 no. 6,
 *   pp. 1291-1298, 2009.
 *   Published tables: https://www.inf.ufrgs.br/~oliveira/pubs_files/CVD_Simulation/CVD_Simulation.html
 *
 * These operate directly on linear-ish sRGB RGB (the paper applies them in the
 * monitor's RGB space). They are the de-facto standard used by Chrome DevTools,
 * Khronos glTF tooling, and most game-engine accessibility layers.
 *
 * ── Daltonization (correction) ────────────────────────────────────────────
 * Daltonization takes the *error* between the original colour and its
 * simulated (as-seen-by-CVD) colour, then redistributes that error into
 * channels the viewer CAN still discriminate. We use the classic
 * Fidaner/Lin/Ozguven shift matrices that map the LMS protan/deutan/tritan
 * error onto remaining channels (the same approach popularised by the
 * "Digital Color Blindness" report, http://www.daltonize.org/). The error
 * redistribution matrix below is the widely-used community constant.
 *
 * All matrices are stored ROW-MAJOR as `[r0,r1,r2, g0,g1,g2, b0,b1,b2]`
 * where out.r = r0*in.r + r1*in.g + r2*in.b, etc. Three.js TSL `mat3()` also
 * takes ROW-MAJOR scalars (it forwards to `new Matrix3()`, whose constructor is
 * row-major — see ColorblindFilterNode.tslMat3FromRowMajor), so these rows are
 * fed straight through with NO transpose.
 */

import type { ColorblindMode } from '../AccessibilityTypes.js';

/** Row-major 3×3 matrix as a flat 9-tuple. */
export type Mat3x3 = readonly [
  number, number, number,
  number, number, number,
  number, number, number,
];

export const IDENTITY_MAT3: Mat3x3 = [
  1, 0, 0,
  0, 1, 0,
  0, 0, 1,
];

/**
 * Machado et al. 2009 severity-1.0 CVD **simulation** matrices (sRGB space).
 * Row-major.
 */
export const SIM_PROTANOPIA: Mat3x3 = [
  0.152286, 1.052583, -0.204868,
  0.114503, 0.786281, 0.099216,
  -0.003882, -0.048116, 1.051998,
];

export const SIM_DEUTERANOPIA: Mat3x3 = [
  0.367322, 0.860646, -0.227968,
  0.280085, 0.672501, 0.047413,
  -0.011820, 0.042940, 0.968881,
];

export const SIM_TRITANOPIA: Mat3x3 = [
  1.255528, -0.076749, -0.178779,
  -0.078411, 0.930809, 0.147602,
  0.004733, 0.691367, 0.303900,
];

/**
 * Achromatopsia (total colour blindness) — Rec.709 luminance projection.
 * Every output channel = perceived luminance. Row-major.
 */
export const SIM_ACHROMATOPSIA: Mat3x3 = [
  0.2126, 0.7152, 0.0722,
  0.2126, 0.7152, 0.0722,
  0.2126, 0.7152, 0.0722,
];

/**
 * Error-redistribution matrix used by daltonization. After computing
 * `err = original - simulated`, the corrected colour is
 * `original + ERROR_SHIFT · err`. This pushes the lost red/green information
 * into the blue and (remaining) channels so a CVD viewer can distinguish it.
 * Row-major; from the daltonize.org reference implementation.
 */
export const DALTONIZE_ERROR_SHIFT: Mat3x3 = [
  0.0, 0.0, 0.0,
  0.7, 1.0, 0.0,
  0.7, 0.0, 1.0,
];

/** Simulation matrix for a given mode (IDENTITY for 'none'). */
export function simulationMatrix(mode: ColorblindMode): Mat3x3 {
  switch (mode) {
    case 'protanopia': return SIM_PROTANOPIA;
    case 'deuteranopia': return SIM_DEUTERANOPIA;
    case 'tritanopia': return SIM_TRITANOPIA;
    case 'achromatopsia': return SIM_ACHROMATOPSIA;
    case 'none':
    default:
      return IDENTITY_MAT3;
  }
}

/** Multiply a row-major 3×3 matrix by an RGB triplet. */
export function applyMat3(m: Mat3x3, r: number, g: number, b: number): [number, number, number] {
  return [
    m[0] * r + m[1] * g + m[2] * b,
    m[3] * r + m[4] * g + m[5] * b,
    m[6] * r + m[7] * g + m[8] * b,
  ];
}

/**
 * CPU reference for the full daltonization correction (used by tests and
 * non-GPU inspector tools). For 'achromatopsia' there is no meaningful
 * "remaining channel" to redistribute into, so we return the luminance
 * simulation directly (matching the GPU node's special-case).
 *
 * @param mode      CVD mode
 * @param strength  0..1 lerp between the original colour and the corrected one
 */
export function daltonizeRgb(
  mode: ColorblindMode,
  r: number, g: number, b: number,
  strength = 1,
): [number, number, number] {
  if (mode === 'none') return [r, g, b];

  if (mode === 'achromatopsia') {
    const [sr, sg, sb] = applyMat3(SIM_ACHROMATOPSIA, r, g, b);
    return [
      r + (sr - r) * strength,
      g + (sg - g) * strength,
      b + (sb - b) * strength,
    ];
  }

  // 1. Simulate how the CVD viewer perceives the colour.
  const [sr, sg, sb] = applyMat3(simulationMatrix(mode), r, g, b);
  // 2. Error = lost information.
  const er = r - sr;
  const eg = g - sg;
  const eb = b - sb;
  // 3. Redistribute error into discriminable channels.
  const [cr, cg, cb] = applyMat3(DALTONIZE_ERROR_SHIFT, er, eg, eb);
  // 4. Add back to original.
  const correctedR = r + cr;
  const correctedG = g + cg;
  const correctedB = b + cb;
  // 5. Lerp by strength.
  return [
    r + (correctedR - r) * strength,
    g + (correctedG - g) * strength,
    b + (correctedB - b) * strength,
  ];
}
